import { useEffect } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { I18nProvider } from '@react-aria/i18n';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { BranchProvider } from '@/contexts/BranchContext';
import { DisplayContextSync } from '@/components/currency/DisplayContextSync';
import { DateRangeProvider } from '@/contexts/DateRangeContext';
import { NotificationProvider } from '@/contexts/NotificationContext';
import { ChatProvider } from '@/contexts/ChatContext';
import { ToastProvider } from '@/contexts/ToastContext';
import { BrandThemeProvider } from '@/theme';
import { AppInstallPrompt } from '@/components';
import { AppRoutes } from '@/routes';
import { TourProvider } from '@/components/tour/TourProvider';
import { Sentry } from '@/lib/sentry';
import {
  useRealtimeConnection,
  useRealtimeInvalidation,
} from '@/hooks/useRealtime';

/**
 * Keeps every open screen current with what other users change.
 *
 * Renders nothing. Holds the clinic-wide change feed open and turns each
 * "record changed" frame into a React Query invalidation, so a screen
 * built on `useQuery` refreshes without knowing this exists. Screens that
 * fetch by hand opt in with `useRealtimeRefetch`.
 */
function RealtimeBridge({ clinicId }: { clinicId: string | undefined }) {
  useRealtimeConnection(clinicId);
  useRealtimeInvalidation();
  return null;
}

function AppContent() {
  const { user, clinic } = useAuth();

  useEffect(() => {
    if (user) {
      // An id only: names and e-mail addresses stay out of Sentry.
      Sentry.setUser({ id: user.id });
      Sentry.setTag('clinic_id', clinic?.id ?? 'none');
    } else {
      Sentry.setUser(null);
    }
  }, [user, clinic]);

  return (
    // BranchProvider is outermost because the two providers below it draw
    // toasts themselves, and a toast words its text with `useFormatters`,
    // which reads the branch (currency, time zone). With the branch provider
    // further in, every toast threw "useBranch must be used within a
    // BranchProvider" and took the whole app to the error screen.
    <BranchProvider>
      <ToastProvider>
        {/* Outermost of the providers that paint: it puts the clinic's brand
            colour onto the document, so everything rendered below it,
            including a toast or a modal that mounts before the routes do, is
            already in the right palette. It renders nothing itself. */}
        <BrandThemeProvider>
          <RealtimeBridge clinicId={clinic?.id} />
          <NotificationProvider userId={user?.id} clinicId={clinic?.id}>
            {/* Money helpers outside React follow the scope currency too. */}
            <DisplayContextSync />
            <DateRangeProvider>
              <ChatProvider>
                <AppInstallPrompt />
                {/* Innermost: a tour swaps the query cache under the routes
                    only, so the providers above keep the live one. */}
                <TourProvider>
                  <AppRoutes />
                </TourProvider>
              </ChatProvider>
            </DateRangeProvider>
          </NotificationProvider>
        </BrandThemeProvider>
      </ToastProvider>
    </BranchProvider>
  );
}

/**
 * Binds react-aria (and therefore every HeroUI field) to the app's language.
 *
 * Without this the aria components read the BROWSER's locale, so on an
 * English-configured laptop an Arabic dashboard rendered its number and
 * currency fields in Latin digits — "EGP 300.00" sitting beside a "ج.م. ٣٠٠"
 * rendered by our own formatter, in the same form. Locale also drives how
 * typed input is parsed, so this is correctness, not only presentation.
 */
function LocaleProvider({ children }: { children: React.ReactNode }) {
  const { i18n } = useTranslation();
  // Region matters for currency and digit shaping, not just language.
  const locale = i18n.language?.startsWith('ar') ? 'ar-EG' : 'en-US';
  return <I18nProvider locale={locale}>{children}</I18nProvider>;
}

function App() {
  return (
    <BrowserRouter>
      <LocaleProvider>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </LocaleProvider>
    </BrowserRouter>
  );
}
// dfdfd
export default App;
